import type { AttendanceResponse } from '../../models/attendance.types';
import type { ManagerReportRow } from '../../models/reports.types';
import type { CostsResponse, ShiftCost } from '../../models/costs.types';
import type { ShiftTip } from '../../models/tips.types';
import { ShiftAuthRequestError } from '../auth/shift-auth.service';
import type { ShiftCostValuesResponse, ShiftManagerReportPage, ShiftPageQuery, ShiftTipsResponse, ShiftTipCalculateInput,ShiftTipRoleCorrectionInput } from 'shared-models';

const backendBaseUrl = process.env.REACT_APP_BACKEND_LOCATION ?? '/__shift_api__';

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(`${backendBaseUrl}${path}`, {
      credentials: 'include',
      ...init,
      headers: init.body ? { 'Content-Type': 'application/json', ...init.headers } : init.headers,
    });
  } catch {
    throw new ShiftAuthRequestError('אין תקשורת לשרת.', 0, true, null);
  }
  const payload = (await response.json().catch(() => null)) as { message?: string | string[] } | null;
  if (!response.ok) {
    const raw = payload?.message;
    throw new ShiftAuthRequestError(
      Array.isArray(raw) ? raw.join(', ') : (raw ?? `HTTP ${response.status}`),
      response.status,
      false,
      payload,
    );
  }
  return payload as T;
};

class ShiftWorkforceService {
  async getAttendance(from: string, to: string, page: ShiftPageQuery = {}): Promise<AttendanceResponse> {
    return request<AttendanceResponse>(`/shift/attendance?from=${from}&to=${to}&offset=${page.offset ?? 0}&limit=${page.limit ?? 50}`);
  }

  async getManagerReport(from: string, to: string, employeePk?: number, page: ShiftPageQuery = {}): Promise<ManagerReportRow[]> {
    return request<ManagerReportRow[]>(
      `/shift/attendance/report?from=${from}&to=${to}${employeePk ? `&employeePk=${employeePk}` : ''}&offset=${page.offset ?? 0}&limit=${page.limit ?? 50}`,
    );
  }

  async getManagerReportPage(from: string, to: string, employeePk?: number, page: ShiftPageQuery = {}): Promise<ShiftManagerReportPage> {
    return request<ShiftManagerReportPage>(`/shift/attendance/report/page?from=${from}&to=${to}${employeePk ? `&employeePk=${employeePk}` : ''}&offset=${page.offset ?? 0}&limit=${page.limit ?? 50}`);
  }

  async getCosts(page: ShiftPageQuery = {}): Promise<CostsResponse> {
    return request<CostsResponse>(`/shift/costs?offset=${page.offset ?? 0}&limit=${page.limit ?? 50}`);
  }

  async getCostValues(costId: number, page: ShiftPageQuery): Promise<ShiftCostValuesResponse> {
    return request<ShiftCostValuesResponse>(`/shift/costs/${costId}/values?offset=${page.offset ?? 0}&limit=${page.limit ?? 50}`);
  }

  async createCost(input: {
    name: string;
    typeCost: 'regular' | 'additional';
    type: ShiftCost['type'];
    additionalCode: ShiftCost['additionalCode'];
    roleIds: number[];
  }): Promise<{ id: number }> {
    return request<{ id: number }>('/shift/costs', { method: 'POST', body: JSON.stringify(input) });
  }

  async setCostActive(id: number, isActive: boolean): Promise<void> {
    return request<void>(`/shift/costs/${id}`, { method: 'PATCH', body: JSON.stringify({ isActive }) });
  }

  async createCostValue(
    id: number,
    input: { startDate: string; endDate: string | null; value: string; roleId: number | null },
  ): Promise<{ id: number }> {
    return request<{ id: number }>(`/shift/costs/${id}/values`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async setShiftTipType(id: number, tipShiftType: 'morning' | 'evening' | null): Promise<void> {
    return request<void>(`/shift/costs/shifts/${id}/tip-type`, {
      method: 'PATCH',
      body: JSON.stringify({ tipShiftType }),
    });
  }

  async getTips(date: string): Promise<ShiftTipsResponse> {
    return request<ShiftTipsResponse>(`/shift/tips/${date}`);
  }

  async calculateTip(
    date: string,
    type: ShiftTip['typeShift'],
    input: ShiftTipCalculateInput,
  ): Promise<unknown> {
    return request(`/shift/tips/${date}/${type}/calculate`, { method: 'POST', body: JSON.stringify(input) });
  }

  async setTipLocked(id: number, locked: boolean): Promise<void> {
    return request<void>(`/shift/tips/${id}/${locked ? 'lock' : 'unlock'}`, { method: 'POST' });
  }

  async setTipRole(date:string,attendanceId:number,input:ShiftTipRoleCorrectionInput):Promise<void> {
    return request<void>(`/shift/tips/${date}/attendance/${attendanceId}/role`,{method:'PATCH',body:JSON.stringify(input)});
  }

  async clearTip(id: number): Promise<void> {
    return request<void>(`/shift/tips/${id}/clear`, { method: 'POST' });
  }
}

export const shiftWorkforceService = new ShiftWorkforceService();
